import _ from 'underscore';

import {assert} from 'Asserts';
import AddSamplesFromBarcodeScannerModal from 'notebook/sample-propagation/AddSamplesFromBarcodeScannerModal';
import {
  AddSamplesToTableTrackingParams,
  trackAddSamples,
} from 'notebook/sample-propagation/util/addSamplesTracking';
import {
  sampleFiltersToWorklistTypes,
  worklistTypeToSampleFilter,
} from 'notebook/sample-propagation/util/SampleTypeWorklistTypeConversions';
import {
  StructuredTableAddOperationOptions,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell, TableColumnType} from 'notebook/tables/TableConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import ScanIntoWorklistModal from 'worklists/barcode-scanning/ScanIntoWorklistModal';

import {dedupeSampleFilters, SampleFilterType} from './SampleFilter';
import getAcceptableSampleFiltersByColumn from './util/getAcceptableSampleFiltersByColumn';
import {makeSampleCellsFromIds} from './util/makeSampleCells';

/**
 * Allows the user to add rows to a table from barcode scanning. This function handles the entire
 * operation, from opening the scan modal through to adding the rows to the table.
 */
export default async function addSamplesFromBarcodeScanner(
  destinationTable: UnknownStructuredTable,
  adapter: UnknownStructuredTableAdapter,
  addSamples: (samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>) => void,
  destinationSpec: {destinationSchemaId: string | null; destinationColumnType: TableColumnType},
  options?: StructuredTableAddOperationOptions,
  trackingParams?: AddSamplesToTableTrackingParams
): Promise<void> {
  const convertedTable = adapter.toTable(destinationTable);
  const destinationSampleFiltersByColumn = getAcceptableSampleFiltersByColumn(convertedTable)
    .map((sampleTypeByColumn) => ({
      ...sampleTypeByColumn,
      columns: sampleTypeByColumn.columnIndices.map((index) => destinationTable.columns[index]),
    }))
    .filter(
      ({sampleFilters}) => !sampleFilters.find((sampleFilter) => sampleFilter.type === SampleFilterType.WELL)
    ) // No such thing as a "well" (plate + position) to scan
    .filter(
      ({columns}) => columns.length === 1 && columns[0].columnType === destinationSpec.destinationColumnType
    ) // Only allow columns that match specified column type
    .filter(({sampleFilters}) =>
      sampleFilters.every(
        ({schemaId}) =>
          schemaId == null ||
          destinationSpec.destinationSchemaId == null ||
          schemaId === destinationSpec.destinationSchemaId
      )
    ); // Only allow filters that match specified destinationSchemaId (or null)
  const acceptableSampleTypes = dedupeSampleFilters(
    // eslint-disable-next-line no-restricted-syntax
    _.flatten(destinationSampleFiltersByColumn.map(({sampleFilters}) => sampleFilters))
  );
  const acceptableScannedItemTypes = sampleFiltersToWorklistTypes(acceptableSampleTypes);
  assert(acceptableScannedItemTypes.length === 1, 'Must specify only one valid type to scan items');

  const scannedItemType = acceptableScannedItemTypes[0];
  const ids = await legacyRunModal(ScanIntoWorklistModal, {worklistType: scannedItemType});
  let addSamplesCommand: {itemIds: Array<string>; destinationColumnsCids: Array<string> | null} | null = null;
  if (ids) {
    addSamplesCommand = await legacyRunModal(AddSamplesFromBarcodeScannerModal, {
      scannedItemType,
      destinationSampleFiltersByColumn,
      destinationTable,
      options,
      scannedItemIds: ids ?? [],
      destinationSchemaId: destinationSpec.destinationSchemaId,
    });
  }

  if (addSamplesCommand) {
    // The modal only sees a filtered subset of Array<StructuredTableColumn>. We need to convert the
    // destination column(s) back into an index into the master `Array<StructuredTableColumn>` array.
    const {itemIds, destinationColumnsCids} = addSamplesCommand;

    assert(
      destinationColumnsCids != null,
      'Must provide valid destination column ids when adding samples to table'
    );
    const destinationColumnIndices = destinationColumnsCids.map((cid) =>
      destinationTable.columns.findIndex((column) => column.cid === cid)
    );
    assert(!destinationColumnIndices.includes(-1), 'Sample destination column cid not found', {
      destinationColumnsCids,
    });

    const sampleType = worklistTypeToSampleFilter(scannedItemType);

    assert(
      destinationColumnIndices.length === 1 && sampleType.type !== SampleFilterType.WELL,
      'Adding wells not support from barcode scanning'
    );

    // The cells we create need to match the column type they're being inserted into
    const columnDefinition = convertedTable.columnConstraints[destinationColumnIndices[0]]!.columnDefinition!;
    const newCells = makeSampleCellsFromIds(itemIds, sampleType.type, columnDefinition);
    assert(
      newCells.length === 1,
      `Must provide exactly 1 column of cells to insert as non-wells, got ${newCells.length} column(s) instead`
    );
    addSamples([{samples: newCells[0].cells, columnIndex: destinationColumnIndices[0]}]);
    if (trackingParams) {
      trackAddSamples({
        ...trackingParams,
        numSamples: newCells[0].cells.length,
        sourceId: null,
        sourceType: 'barcode_scan',
      });
    }
  }
}
